import type { ApexOptions } from 'apexcharts';

import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

interface ISubscriptionDistributionChart {
  label: string;
  value: number;
  color: string;
}

interface ISubscriptionDistributionChartProps {
  data: ISubscriptionDistributionChart[];
}

const SubscriptionDistributionChart: React.FC<
  ISubscriptionDistributionChartProps
> = ({ data }) => {
  const series = useMemo(() => data.map((item) => item.value), [data]);
  const labels = useMemo(() => data.map((item) => item.label), [data]);
  const colors = useMemo(() => data.map((item) => item.color), [data]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'donut',
        fontFamily: 'Aptos, sans-serif',
        toolbar: { show: false },
      },
      labels,
      colors,
      dataLabels: { enabled: false },
      legend: { show: false },
      stroke: { width: 6 },
      plotOptions: {
        pie: {
          donut: {
            size: '50%',
            labels: { show: false },
          },
          expandOnClick: false,
        },
      },
      tooltip: {
        enabled: true,
        custom: ({ seriesIndex }: { seriesIndex: number }) => {
          const item = data[seriesIndex];
          if (!item) return '';
          return `
            <div style="background:white;padding:6px 12px;white-space:nowrap;font-family:Aptos,sans-serif;">
              <div style="display:flex;align-items:center;gap:8px;">
                <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${item?.color};flex-shrink:0;"></span>
                <span style="font-size:14px;color:#525866;">${item?.label}</span>
                <span style="font-size:12px;font-weight:500;color:#181B25;">${item?.value}%</span>
              </div>
            </div>`;
        },
      },
      states: {
        hover: { filter: { value: 0.85, type: 'lighten' } },
        active: { filter: { type: 'darken', value: 0.85 } },
      },
    }),
    [labels, colors, data]
  );

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
      {/* Donut */}
      <div className="shrink-0">
        <ReactApexChart
          options={options}
          series={series}
          type="donut"
          width={200}
          height={200}
        />
      </div>

      {/* Legend */}
      <div className="flex flex-col gap-3 flex-1 w-full sm:w-auto sm:max-w-50">
        {data?.map((item) => (
          <div key={item?.label} className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-2 h-2 rounded-sm shrink-0"
                style={{ backgroundColor: item?.color }}
              />
              <span className="text-sm text-neutral-primary font-medium truncate">
                {item?.label}
              </span>
            </div>
            <span className="text-sm font-semibold text-neutral-primary shrink-0">
              {item?.value}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SubscriptionDistributionChart;
